import { useAccountContext } from '@/context/account'
import { shortenAddress } from '@/utils/address/shortenAddress'
import { copyToClipboard } from '@/utils/copyToClipboard'
import { getNetworkName } from '@/utils/network/getNetworkName'
import type { Network } from '@vaultsfyi/common/types'
import { formatTokenValue } from '@vaultsfyi/common/utils'
import { Button } from '../common/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogContentWrapper,
  DialogHeader,
  DialogHeaderWrapper,
  DialogScrollArea,
  DialogTitle,
} from '../common/dialog'
import { NetworkImage } from '../common/networkImage'
import { Note } from '../common/note'
import { QRCode } from '../common/qrCode'
import { Separator } from '../common/separator'
import { TokenImage } from '../common/tokenImage'
import { CopyIcon } from '../icons/copy'
import type { TabConfig } from './useTabsConfig'

interface FundWalletOnSupplyProps {
  network: Network
  asset: { symbol: string; decimals: number }
  walletBalance: string
  depositConfig: TabConfig
  open: boolean
  closeModal: () => void
}

export function FundWalletOnSupply({
  network,
  asset,
  walletBalance,
  depositConfig,
  open,
  closeModal,
}: FundWalletOnSupplyProps) {
  const { account } = useAccountContext()
  const { getParsedAmount, form } = depositConfig
  const parsedDepositAmount = getParsedAmount(form.watch('amount') || '0')
  const sufficientBalance = BigInt(walletBalance) >= BigInt(parsedDepositAmount)

  return (
    <Dialog open={open} onOpenChange={() => closeModal()}>
      <DialogContent position="right">
        <DialogBody>
          <DialogHeaderWrapper>
            <DialogHeader>
              <DialogTitle>Fund your wallet</DialogTitle>
            </DialogHeader>
            <Separator />
          </DialogHeaderWrapper>
          <DialogScrollArea>
            <DialogContentWrapper className="pt-4">
              {sufficientBalance ? (
                <Note color="green">
                  <p>You are ready to finish your deposit!</p>
                </Note>
              ) : (
                <Note color="orange" className="flex flex-col gap-2">
                  <span className="font-medium text-lg">Insufficient funds</span>
                  <p>
                    Fund your wallet with{' '}
                    <span className="font-semibold text-warning">
                      {formatTokenValue({ value: parsedDepositAmount, decimals: asset.decimals })} {asset.symbol}
                    </span>{' '}
                    to complete your deposit.
                  </p>
                </Note>
              )}

              <p>
                Wallet balance:{' '}
                <span className="font-medium">
                  {formatTokenValue({ value: walletBalance, decimals: asset.decimals })} {asset.symbol}
                </span>
              </p>

              <div>
                <p className="font-medium text-heading-foreground">You can fund your account by:</p>
                <ul className="mt-2 list-outside list-disc space-y-2 pl-4">
                  <li>sending funds from another crypto wallet (Metamask, etc)</li>
                  <li>sending funds from a centralized exchange or payment provider (Coinbase, Binance, Robinhood)</li>
                </ul>
              </div>

              {account && (
                <>
                  <Separator />
                  <div className="flex flex-col gap-2">
                    <p className="flex flex-wrap items-center gap-2">
                      Supply
                      <span className="flex items-center gap-1">
                        <TokenImage symbol={asset.symbol} />
                        <span className="capitalize">{asset.symbol}</span>
                      </span>
                      on
                      <span className="flex items-center gap-1">
                        <NetworkImage network={network} />
                        <span className="capitalize">{getNetworkName(network)}</span>
                      </span>
                    </p>
                    <p>Copy address or use QR below to send funds to your wallet</p>
                    <div className="flex items-center gap-2">
                      <p className="text-heading-foreground">{shortenAddress(account)}</p>
                      <Button
                        variant="input"
                        size="icon"
                        onClick={() => copyToClipboard(account)}
                        className="size-9 min-w-9 rounded-full bg-page"
                      >
                        <CopyIcon size={20} />
                      </Button>
                    </div>
                    <div className="mx-auto w-fit rounded-2xl border bg-page p-2">
                      <QRCode data={account} />
                    </div>
                  </div>
                </>
              )}
            </DialogContentWrapper>
          </DialogScrollArea>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
